import { useCallback, useEffect, useRef, useState } from "react"

interface UseInfiniteScrollOptions {
  totalPages: number
  onLoadMore: (page: number) => Promise<void>
}

export function useInfiniteScroll({
  totalPages,
  onLoadMore,
}: UseInfiniteScrollOptions) {
  const [currentPage, setCurrentPage] = useState(1)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const fetchingRef = useRef(false)

  const hasMore = currentPage < totalPages

  const loadNext = useCallback(async () => {
    if (fetchingRef.current || !hasMore) return
    fetchingRef.current = true
    setIsFetchingMore(true)

    const nextPage = currentPage + 1
    try {
      await onLoadMore(nextPage)
      setCurrentPage(nextPage)
    } finally {
      fetchingRef.current = false
      setIsFetchingMore(false)
    }
  }, [currentPage, hasMore, onLoadMore])

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const sentinel = sentinelRef.current
    if (!sentinel) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNext()
        }
      },
      { rootMargin: "200px" },
    )

    observerRef.current.observe(sentinel)

    return () => {
      observerRef.current?.disconnect()
    }
  }, [loadNext])

  const reset = useCallback(() => {
    setCurrentPage(1)
    fetchingRef.current = false
    setIsFetchingMore(false)
  }, [])

  return { sentinelRef, isFetchingMore, hasMore, reset }
}
